import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Redirect } from 'react-router';
import { generate } from 'randomstring';
import { useForm } from 'react-hook-form';
import { CookieStorage } from 'cookie-storage';
import profile from "../../public/test.json";
import styled from 'styled-components';
import { Form, Card, Button, Input, Message, Icon, Checkbox } from 'semantic-ui-react'

import { createStorage } from './storage/encrypted';

const Container = styled.form`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh
`;

const PIN_KEY = '_p';

const Decrypt = ({ locale, children }) => {
    const [loading, setLoading] = useState(true);
    const [validity, setValidity] = useState(false);
    const [retries, setRetries] = useState(0);
    const [changed, setChanged] = useState(false);
    const [error, setError] = useState();
    const cookieStorage = useMemo(() => new CookieStorage(), []);
    const [access, setAccess] = useState(Buffer.from(cookieStorage.getItem(PIN_KEY) || '', 'base64').toString('utf8'));

    const accessRef = useRef(access);
    accessRef.current = access;
    const validityRef = useRef(validity);
    validityRef.current = validity;

    const storage = useMemo(() => createStorage(accessRef), [accessRef]);

    const pin = cookieStorage.getItem(PIN_KEY);

    const checkValidity = useMemo(() => async (accessKey) => {
        if (accessKey.length < 6) {
            setError({ content: 'Must be 6 or longer', pointing: 'below' });
            return;
        }

        const tempStorage = createStorage({ current: accessKey });

        const testVal = await tempStorage.getItem('__creator');

        if (typeof testVal === 'undefined' || /\w{3}:\w{3}:\w{3}/.test(testVal)) {
            cookieStorage.setItem(PIN_KEY, Buffer.from(accessKey).toString('base64'), {
                path: '/',
                expires: new Date(Date.now() - 1000),
                secure: process.NODE_ENV === 'production',
                sameSite: 'Strict'
            });
            await tempStorage.setItem('__creator', `${generate(3)}:${generate(3)}:${generate(3)}`)
            setAccess(accessKey);
            setValidity(true);

            setError(null);
            return;
        }

        if (retries >= 3) {
            setLoading(true);
            storage.clear();
            window.location.reload();
        } else {
            if (retries == 2) {
                alert('Next attempt will delete all data');
            }
            setRetries(retries + 1);

            setError({ content: `Invalid Pin ${retries + 1}/3`, pointing: 'below' });
        }
        return;
    }, [storage, cookieStorage, retries, setError, setValidity, setAccess]);

    useEffect(() => {
        storage.getItem('__creator').then(val => setChanged(typeof val !== 'undefined'));

        if (access) {
            checkValidity(access)
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [storage]);

    useEffect(() => {
        const interval = setInterval(() => {
            const pin = cookieStorage.getItem(PIN_KEY);

            if (pin && validityRef.current) {
                cookieStorage.setItem(PIN_KEY, pin, {
                    path: '/',
                    expires: new Date(Date.now() + 3e4),
                    secure: process.NODE_ENV === 'production',
                    sameSite: 'Strict'
                });
            }
        }, 1e4);

        return () => clearInterval(interval);
    }, [validityRef, cookieStorage]);

    const checkValidityRef = useRef(checkValidity);
    checkValidityRef.current = checkValidity;

    const { register, handleSubmit, errors } = useForm(); // initialise the hook

    if (validity) {
        return children({ storage });
    }

    return (
        <Form as={Container} loading={loading} error={!!error}
            onSubmit={handleSubmit(({ pin }) => checkValidityRef.current(pin))}>
            <Card>
                <Card.Content>
                    <Card.Header>OpeReady</Card.Header>
                    <Card.Meta>{changed ? 'Enter Pin Code' : 'Create New Pin Code'}</Card.Meta>
                    <Card.Description>
                        <Form.Field error={!!error}>
                            <label>Pin Code</label>
                            <Input name="pin" placeholder='pin' type='password' defaultValue={pin}
                                ref={ref => register()(ref && ref.inputRef.current)} />
                        </Form.Field>
                    </Card.Description>
                </Card.Content>
                <Message attached='bottom' error>
                    {error && error.content}
                </Message>
                <Card.Content extra>
                    <div className='ui two buttons'>
                        <Button primary type='submit'>Proceed</Button>
                    </div>
                </Card.Content>
            </Card>

        </Form>
    );
};

export default Decrypt;
